/**
 * The questions the pricing page answers.
 *
 * Separate from the route so the FAQ schema and the rendered `<dl>` read one
 * array — the rule the rest of the site follows, and the only way to be sure
 * structured data describes content a visitor can actually find.
 *
 * Each answer is checked against the product:
 *  - view limits: nothing in the API or the widget bundles counts impressions
 *  - widget count: `PLAN_DEFINITIONS[*].limits.widgets` is `-1` on every plan
 *  - branding: `removeBranding` is gated on `planTier(plan) !== 'free'`
 *  - auto-sync: `hasAutoSync` is Advanced and above
 *  - Wix billing: `BILLING_PROVIDERS` includes `wix`, and Wix installs bill there
 *  - the trial: `TRIAL_PERIOD_DAYS` is 14, `qualifiesForTrial` is
 *    `!billing.hadTrial` (so once per workspace), the checkout session sets no
 *    `payment_method_collection` (so Stripe takes a card up front), and
 *    `hadTrial` is never set for Wix installs
 */
export const PRICING_FAQS: { q: string; a: string }[] = [
  {
    q: 'Is there a limit on how many people can see my widget?',
    a: 'No. ReviewsJet does not count or cap widget views on any plan, including free. Plans are limited by how many reviews you publish and how many sources you connect, so a page that suddenly does well does not cost you anything extra.',
  },
  {
    q: 'Which widget layouts do I get on the free plan?',
    a: 'All of them. Carousel, masonry, marquee, grid, list and popup are available on every plan, and there is no limit on how many widgets you create. Customising their appearance is a paid feature.',
  },
  {
    q: 'What does "5 published reviews" mean on the free plan?',
    a: 'It is how many reviews can be live on your website at once, not how many you can import. You can import everything, then choose the five you want showing and swap them whenever you like.',
  },
  {
    q: 'When do my reviews update on their own?',
    a: 'Automatic daily sync starts on Advanced. On Free and Professional you refresh a connected source yourself, which takes a click — fine for one site, and real work if you are running several.',
  },
  {
    q: 'Do I need a card to start?',
    a: 'No. The free plan does not ask for one, and you can build a widget on the free tools without an account at all. A card is only taken when you subscribe to a paid plan.',
  },
  {
    q: 'Is there a free trial on the paid plans?',
    a: 'Yes — 14 days, starting when you subscribe. Your card is taken at checkout and charged when the trial ends, so cancelling before then costs nothing. It runs once per workspace, and Wix installations are trialled and billed through Wix rather than through us.',
  },
  {
    q: 'How does billing work if I installed from Wix?',
    a: 'Wix handles it. An app distributed through the Wix App Market is required to charge through Wix, so your subscription, invoices and refunds all live in your Wix account rather than with us.',
  },
  {
    q: 'Can I remove the ReviewsJet branding?',
    a: 'Yes, on any paid plan. That is the only branding control — the widget is not otherwise white-labelled, and there is no reseller programme.',
  },
];
